import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, EyeOff, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { RESORTS } from "@/data/resorts";
import { clearTestimonialsCache } from "@/lib/testimonials";

const EMPTY = { client_name: "", resort: "", text: "", travel_date: "", photo_url: "", approved: false };

const resortName = (slug) => RESORTS.find((r) => r.slug === slug)?.name || slug || "—";

/**
 * Gestão de depoimentos REAIS de clientes do portal.
 * Só depoimentos com "Aprovado" aparecem no site público; enquanto não
 * houver nenhum aprovado, a seção permanece oculta automaticamente.
 */
export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/api/admin/testimonials");
            setItems(data);
        } catch {
            toast.error("Não foi possível carregar os depoimentos.");
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingId) {
                await api.patch(`/api/admin/testimonials/${editingId}`, form);
            } else {
                await api.post("/api/admin/testimonials", form);
            }
            clearTestimonialsCache();
            setForm(EMPTY);
            setEditingId(null);
            toast.success(editingId ? "Depoimento atualizado." : "Depoimento cadastrado.");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Não foi possível salvar o depoimento.");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (t) => {
        setEditingId(t.id);
        setForm({
            client_name: t.client_name,
            resort: t.resort,
            text: t.text,
            travel_date: t.travel_date,
            photo_url: t.photo_url,
            approved: t.approved,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleToggle = async (t) => {
        try {
            await api.patch(`/api/admin/testimonials/${t.id}`, {
                client_name: t.client_name,
                resort: t.resort,
                text: t.text,
                travel_date: t.travel_date,
                photo_url: t.photo_url,
                approved: !t.approved,
            });
            clearTestimonialsCache();
            toast.success(t.approved ? "Depoimento ocultado do site." : "Depoimento publicado no site.");
            load();
        } catch {
            toast.error("Não foi possível alterar a publicação.");
        }
    };

    const handleDelete = async (t) => {
        if (!window.confirm(`Excluir o depoimento de ${t.client_name}?`)) return;
        try {
            await api.delete(`/api/admin/testimonials/${t.id}`);
            clearTestimonialsCache();
            toast.success("Depoimento excluído.");
            load();
        } catch {
            toast.error("Não foi possível excluir.");
        }
    };

    return (
        <div className="mt-8" data-testid="admin-testimonials">
            <form onSubmit={handleSubmit} className="rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm sm:p-8">
                <h2 className="font-serif text-xl font-semibold text-forest-deep">
                    {editingId ? "Editar depoimento" : "Cadastrar depoimento real"}
                </h2>
                <p className="mt-1 text-xs text-stone-500">
                    Cadastre apenas avaliações reais de clientes que fecharam pelo portal. Foto somente com autorização do cliente.
                </p>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="tm-name">Nome do cliente *</label>
                        <input id="tm-name" data-testid="testimonial-input-name" required value={form.client_name} onChange={set("client_name")} className="field-input mt-1.5" placeholder="Ex.: Família Becker" />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="tm-resort">Resort visitado</label>
                        <select id="tm-resort" data-testid="testimonial-input-resort" value={form.resort} onChange={set("resort")} className="field-input mt-1.5">
                            <option value="">Não informado</option>
                            {RESORTS.map((r) => (
                                <option key={r.slug} value={r.slug}>{r.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="tm-text">Texto da avaliação *</label>
                        <textarea id="tm-text" data-testid="testimonial-input-text" required rows={3} value={form.text} onChange={set("text")} className="field-input mt-1.5 resize-none" placeholder="Depoimento do cliente, com as palavras dele(a)." />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="tm-date">Data da viagem</label>
                        <input id="tm-date" data-testid="testimonial-input-date" value={form.travel_date} onChange={set("travel_date")} className="field-input mt-1.5" placeholder="Ex.: 07/2026" />
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500" htmlFor="tm-photo">URL da foto (opcional, com autorização)</label>
                        <input id="tm-photo" data-testid="testimonial-input-photo" type="url" value={form.photo_url} onChange={set("photo_url")} className="field-input mt-1.5" placeholder="https://..." />
                    </div>
                </div>

                <label className="mt-5 flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-forest-deep">
                    <input type="checkbox" data-testid="testimonial-input-approved" checked={form.approved} onChange={set("approved")} className="h-4 w-4 accent-forest-emerald" />
                    Aprovado — visível no site público
                </label>

                <div className="mt-6 flex gap-3">
                    <button type="submit" data-testid="testimonial-save-btn" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-forest-emerald px-6 py-3 text-xs font-semibold text-white transition-colors hover:bg-forest-dark disabled:opacity-50">
                        <Check className="h-4 w-4" />
                        {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar depoimento"}
                    </button>
                    {editingId && (
                        <button type="button" data-testid="testimonial-cancel-btn" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded-full border border-[#E5DEC9] bg-white px-6 py-3 text-xs font-semibold text-forest-deep transition-colors hover:bg-forest-deep/5">
                            Cancelar edição
                        </button>
                    )}
                </div>
            </form>

            <div className="mt-8 space-y-4">
                {items.length === 0 ? (
                    <div className="rounded-3xl border border-dashed border-[#D5CCA8] bg-white/60 px-8 py-12 text-center" data-testid="testimonials-empty">
                        <p className="font-serif text-lg font-semibold text-forest-deep">Nenhum depoimento cadastrado</p>
                        <p className="mt-1.5 text-sm text-stone-500">A seção de prova social permanece oculta no site até o primeiro depoimento aprovado.</p>
                    </div>
                ) : (
                    items.map((t) => (
                        <article key={t.id} className="flex flex-col gap-4 rounded-3xl border border-[#E5DEC9] bg-white p-6 shadow-sm sm:flex-row sm:items-start sm:justify-between" data-testid={`testimonial-card-${t.id}`}>
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <p className="font-serif text-base font-semibold text-forest-deep">{t.client_name}</p>
                                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${t.approved ? "bg-[#EAF4EE] text-[#164B3A]" : "bg-stone-100 text-stone-500"}`} data-testid={`testimonial-status-${t.id}`}>
                                        {t.approved ? "Publicado" : "Oculto"}
                                    </span>
                                </div>
                                <p className="mt-1 text-[11px] text-stone-400">{resortName(t.resort)}{t.travel_date && ` · Viagem em ${t.travel_date}`}</p>
                                <p className="mt-2.5 text-sm leading-relaxed text-stone-600">“{t.text}”</p>
                            </div>
                            <div className="flex shrink-0 gap-2">
                                <button type="button" data-testid={`testimonial-toggle-${t.id}`} onClick={() => handleToggle(t)} title={t.approved ? "Ocultar do site" : "Publicar no site"} className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${t.approved ? "border-[#E5DEC9] text-stone-500 hover:bg-stone-50" : "border-forest-emerald text-forest-emerald hover:bg-forest-emerald hover:text-white"}`}>
                                    {t.approved ? <EyeOff className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                                </button>
                                <button type="button" data-testid={`testimonial-edit-${t.id}`} onClick={() => handleEdit(t)} title="Editar" className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E5DEC9] text-forest-deep transition-colors hover:bg-forest-deep/5">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button type="button" data-testid={`testimonial-delete-${t.id}`} onClick={() => handleDelete(t)} title="Excluir" className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors hover:bg-red-50">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </article>
                    ))
                )}
            </div>
        </div>
    );
}
